import { MediaInfoPreviewProps } from '@/components/IngestManagement/FileIngest/FileIngestMediaInfo';
import { TechMetadataFieldValue } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Badge, Box, Button, Paper, Tab, Tabs } from '@mui/material';
import React, { useMemo } from 'react';
import { PolarisIcon } from '@tmt-insights/oms_fe_core/columns';
import MediaInfo from './MediaInfo';
import MediaInfoCompareModal from './MediaInfoCompareModal';
import Specifications from './Specifications';

enum MediaInfoTabs {
  MEDIA_INFO = 0,
  VIDEO_SPECS = 1
}

type CompareData = {
  actual?: TechMetadataFieldValue;
  expected?: TechMetadataFieldValue;
  label: string;
  validated: boolean;
};

export type CompareSection = {
  sectionName: string;
  isExtra?: boolean;
  fields: Array<CompareData & { field: string }>;
};

const getScopedFieldKey = (sectionIndex: number, fieldKey: string) => `${sectionIndex}::${fieldKey}`;

const BBCFileIngestMediaInfo = ({ data }: MediaInfoPreviewProps) => {
  const [tab, setTab] = React.useState(MediaInfoTabs.MEDIA_INFO);
  const [showCompareModal, setShowCompareModal] = React.useState(false);

  const { media_info: mediaInfos = [], specifications: mediaSpecs = [], failed_validation: failedValidationsList = [] } = data?.tech_metadata || {};
  const fallbackSectionPrefix = data.file_type?.toUpperCase() || 'MEDIA';

  // For compare modal, collect fields grouped by section so each audio track / spec group is identifiable
  const uniqueFields = useMemo(() => {
    const sectionMap: Array<{ sectionName: string; fields: Record<string, CompareData> }> = [];

    mediaInfos.forEach((mediaInfo, index) => {
      const sectionName = mediaInfo.name || `${fallbackSectionPrefix} ${index + 1}`;
      sectionMap[index] = sectionMap[index] || { sectionName, fields: {} };
      mediaInfo?.fields?.forEach((field) => {
        sectionMap[index].fields[field.field] = { actual: field.value, label: field.label, validated: false };
      });
    });

    mediaSpecs.forEach((mediaSpec, index) => {
      if (!sectionMap[index]) {
        const sectionName = mediaSpec.name || `${fallbackSectionPrefix} ${index + 1}`;
        sectionMap[index] = { sectionName, fields: {} };
      }
      mediaSpec?.fields?.forEach((field) => {
        const key = field.media_info_field || field.field;
        sectionMap[index].fields[key] = {
          ...(sectionMap[index].fields[key] ?? {}),
          label: field.label,
          expected: field.expected,
          validated: field.validated
        };
      });
    });

    return sectionMap.map(({ sectionName, fields }, index) => ({
      sectionName,
      isExtra: index >= mediaSpecs.length || index >= mediaInfos.length,
      fields: Object.keys(fields)
        .map((field) => ({ field, ...fields[field] }))
        .sort((a, b) => {
          if (a.validated && !b.validated) return -1;
          if (!a.validated && b.validated) return 1;
          return 0;
        })
    }));
  }, [mediaInfos, mediaSpecs, fallbackSectionPrefix]);

  // Normalize failed validations to section-scoped keys so repeated field names across tracks do not collide.
  const normalizedMismatchedFields = useMemo(() => {
    const mismatchSet = new Set<string>();

    failedValidationsList.forEach((failedFields, sectionIndex) => {
      failedFields?.forEach((failedField) => mismatchSet.add(getScopedFieldKey(sectionIndex, failedField)));
    });

    mediaSpecs.forEach((mediaSpec, sectionIndex) => {
      mediaSpec?.fields?.forEach((field) => {
        const specField = field.field;
        const mediaInfoField = field.media_info_field;
        const scopedSpecField = getScopedFieldKey(sectionIndex, specField);
        const scopedMediaInfoField = mediaInfoField ? getScopedFieldKey(sectionIndex, mediaInfoField) : null;

        if (scopedMediaInfoField && mismatchSet.has(scopedSpecField)) {
          mismatchSet.add(scopedMediaInfoField);
        }

        if (scopedMediaInfoField && mismatchSet.has(scopedMediaInfoField)) {
          mismatchSet.add(scopedSpecField);
        }
      });
    });

    // Sections beyond the spec count are entirely unexpected — mark all their fields as mismatched
    mediaInfos.slice(mediaSpecs.length).forEach((mediaInfo, extraIdx) => {
      const sectionIndex = mediaSpecs.length + extraIdx;
      mediaInfo?.fields?.forEach((field) => {
        mismatchSet.add(getScopedFieldKey(sectionIndex, field.field));
      });
    });

    // Spec sections beyond the media info count — expected tracks that are missing entirely
    mediaSpecs.slice(mediaInfos.length).forEach((mediaSpec, extraIdx) => {
      const sectionIndex = mediaInfos.length + extraIdx;
      mediaSpec?.fields?.forEach((field) => {
        if (field.validated) {
          mismatchSet.add(getScopedFieldKey(sectionIndex, field.media_info_field || field.field));
          mismatchSet.add(getScopedFieldKey(sectionIndex, field.field));
        }
      });
    });

    return Array.from(mismatchSet);
  }, [failedValidationsList, mediaSpecs, mediaInfos]);

  const visibleMismatchedFields = useMemo(() => {
    const availableFieldKeys = new Set(
      uniqueFields.flatMap((section, sectionIndex) => section.fields.map((field) => getScopedFieldKey(sectionIndex, field.field)))
    );
    return normalizedMismatchedFields.filter((fieldKey) => availableFieldKeys.has(fieldKey));
  }, [normalizedMismatchedFields, uniqueFields]);

  if (!data?.tech_metadata) {
    return null;
  }

  return (
    <Box>
      <Header>
        <TabsStyled value={tab} onChange={(_event, newValue) => setTab(newValue)} aria-label="Media Info Tabs">
          <Tab label={`MEDIA INFO - ${data.file_type?.toUpperCase()}`} />
          <Tab label={`${data.file_type?.toUpperCase()} SPECS`} />
        </TabsStyled>
        <Button
          startIcon={<PolarisIcon width={18} height={18} icon="compare_arrows" />}
          endIcon={visibleMismatchedFields.length ? <StyledBadge badgeContent={visibleMismatchedFields.length} color="warning" /> : null}
          variant="outlined"
          color="primary"
          onClick={() => setShowCompareModal(true)}
        >
          Compare
        </Button>
      </Header>
      <Paper sx={{ p: 2 }}>
        {tab === 0 ? (
          // Render all mediaInfos, one under another
          mediaInfos.length ? (
            mediaInfos.map((mediaInfo, idx) => (
              <Box key={`${mediaInfo.name}-${idx}`} mb={2}>
                <MediaInfo
                  fileType={data.file_type}
                  mediaInfo={{ ...mediaInfo, name: mediaInfo.name || `${fallbackSectionPrefix} ${idx + 1}` }}
                  mismatchedFields={visibleMismatchedFields
                    .filter((fieldKey) => fieldKey.startsWith(`${idx}::`))
                    .map((fieldKey) => fieldKey.slice(`${idx}::`.length))}
                />
              </Box>
            ))
          ) : (
            <span style={{ opacity: 0.3, fontStyle: 'italic' }}>No Media Info available</span>
          )
        ) : // Render all mediaSpecs, one under another
        mediaSpecs.length ? (
          mediaSpecs.map((mediaSpec, idx) => (
            <Box key={`${mediaSpec.name}-${idx}`} mb={2}>
              <Specifications specInfo={mediaSpec} />
            </Box>
          ))
        ) : (
          <span style={{ opacity: 0.3, fontStyle: 'italic' }}>No Media Specs available</span>
        )}
      </Paper>
      <MediaInfoCompareModal
        fileName={data.filename}
        fileType={data.file_type}
        open={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        mismatchedFields={visibleMismatchedFields}
        uniqueFields={uniqueFields}
      />
    </Box>
  );
};

export default BBCFileIngestMediaInfo;

const TabsStyled = styled(Tabs)`
  && {
    button,
    .Mui-selected {
      color: ${(props) => props.theme.palette.tertiary.contrastText};
      padding: ${(props) => props.theme.spacing(1.5, 0)};
      margin-right: ${(props) => props.theme.spacing(4)};
    }

    .MuiTabs-indicator {
      background-color: ${(props) => props.theme.palette.tertiary.contrastText};
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledBadge = styled(Badge)`
  margin-right: ${(props) => props.theme.spacing(1)};
  margin-left: ${(props) => props.theme.spacing(1)};
`;
